if (!customElements.get('mc-card-diopter')) {
  customElements.define(
    'mc-card-diopter',
    class McCardDiopter extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.variantInput = this.querySelector('input[name="id"]');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.submitText = this.submitButton?.querySelector('span');
        this.baseSelects = Array.from(this.querySelectorAll('[data-mc-card-option]'));
        this.bundleSelects = Array.from(this.querySelectorAll('[data-mc-bundle-option]'));
        this.bundle3Selects = Array.from(this.querySelectorAll('[data-mc-bundle3-option]'));
        this.baseFields = this.querySelector('[data-base-fields]');
        this.bundleFields = this.querySelector('[data-bundle-fields]');
        this.bundle3Fields = this.querySelector('[data-bundle3-fields]');
        this.packMode = 'single';
        this.abortController = null;
        this.requestNumber = 0;

        if (!this.form || !this.variantInput || !this.submitButton || !this.baseSelects.length) return;

        this.baseSelects.concat(this.bundleSelects, this.bundle3Selects).forEach((select) => {
          select.addEventListener('change', () => this.resolveVariant());
        });
        this.addEventListener('click', (event) => {
          const packButton = event.target.closest('[data-pack-mode]');
          if (!packButton) return;
          this.setPackMode(packButton.dataset.packMode);
        });
        this.setPackMode('single');
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      async resolveVariant() {
        this.clearError();
        const activeSelects = this.packMode === 'bundle3'
          ? this.bundle3Selects
          : this.packMode === 'bundle'
            ? this.bundleSelects
            : this.baseSelects;
        if (!activeSelects.length || activeSelects.some((select) => !select.value)) {
          this.setState('choose');
          return;
        }

        const optionValueIds = this.getOptionValueIds(activeSelects);
        if (optionValueIds.some((id) => !id)) {
          this.setError('Please choose a valid power.');
          this.setState('unavailable');
          return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();
        const currentRequest = ++this.requestNumber;
        this.setState('loading');

        try {
          const productUrl = this.packMode === 'bundle3'
            ? this.dataset.bundle3Url
            : this.packMode === 'bundle'
              ? this.dataset.bundleUrl
              : this.dataset.productUrl;
          const variantId = await this.resolveOptionValues(productUrl, optionValueIds);
          if (currentRequest !== this.requestNumber) return;

          this.variantInput.value = variantId;
          this.variantInput.disabled = false;
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.setState('ready');
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.setError('Could not check this power. Please try again.');
          this.setState('unavailable');
        }
      }

      getOptionValueIds(selects) {
        return selects.map((select) => select.selectedOptions[0]?.dataset.optionValueId);
      }

      async resolveOptionValues(productUrl, optionValueIds) {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('option_values', optionValueIds.join(','));
        const response = await fetch(url.toString(), {
          signal: this.abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const productInfo = html.querySelector('product-info');
        const resolvedInput = productInfo?.querySelector('input.product-variant-id[name="id"]');
        const resolvedButton = productInfo?.querySelector('[id^="ProductSubmitButton-"]');
        const variantId = resolvedInput?.value;
        if (!variantId || resolvedInput.disabled || resolvedButton?.disabled) {
          throw new Error('Selected prescription is sold out.');
        }
        return variantId;
      }

      setPackMode(mode) {
        if (!['single', 'bundle', 'bundle3'].includes(mode)) mode = 'single';
        if (mode === 'bundle' && (!this.dataset.bundleUrl || !this.bundleSelects.length)) return;
        if (mode === 'bundle3' && (!this.dataset.bundle3Url || !this.bundle3Selects.length)) return;
        this.packMode = mode;
        this.classList.toggle('is-bundle-mode', mode !== 'single');
        if (this.baseFields) this.baseFields.hidden = mode !== 'single';
        if (this.bundleFields) this.bundleFields.hidden = mode !== 'bundle';
        if (this.bundle3Fields) this.bundle3Fields.hidden = mode !== 'bundle3';

        [this.baseSelects, this.bundleSelects, this.bundle3Selects].forEach((selects, index) => {
          const selectMode = ['single', 'bundle', 'bundle3'][index];
          if (selectMode !== mode) selects.forEach((select) => { select.value = ''; });
        });

        this.querySelectorAll('[data-pack-mode]').forEach((button) => {
          const active = button.dataset.packMode === mode;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });

        const quantityInput = this.form.querySelector('input[name="quantity"]');
        if (quantityInput) quantityInput.value = '1';

        const dialog = this.closest('dialog');
        const price = dialog?.querySelector('[data-quick-add-price]');
        const comparePrice = dialog?.querySelector('[data-quick-add-compare-price]');
        const priceRow = dialog?.querySelector('.co-quick-add-price-row');
        const meta = dialog?.querySelector('[data-quick-add-meta]');
        const title = dialog?.querySelector('[data-quick-add-title]');
        const baseMedia = dialog?.querySelector('[data-quick-add-media-base]');
        const bundleMedia = dialog?.querySelector('[data-quick-add-media-bundle]');
        const bundle3Media = dialog?.querySelector('[data-quick-add-media-bundle3]');
        const baseDescription = dialog?.querySelector('[data-quick-add-description-base]');
        const bundleDescription = dialog?.querySelector('[data-quick-add-description-bundle]');
        const bundle3Description = dialog?.querySelector('[data-quick-add-description-bundle3]');
        const baseDetails = dialog?.querySelector('[data-quick-add-details-base]');
        const bundleDetails = dialog?.querySelector('[data-quick-add-details-bundle]');
        const bundle3Details = dialog?.querySelector('[data-quick-add-details-bundle3]');
        const fullLink = dialog?.querySelector('[data-quick-add-full-link]');
        const suffix = mode === 'single' ? 'base' : mode;
        if (price) price.textContent = this.dataset[`${suffix}Price`] || '';
        if (comparePrice) {
          const compareValue = this.dataset[`${suffix}ComparePrice`] || '';
          comparePrice.textContent = compareValue;
          comparePrice.hidden = !compareValue;
          if (priceRow) priceRow.classList.toggle('has-compare-price', Boolean(compareValue));
        }
        if (meta) meta.textContent = this.dataset[`${suffix}Label`] || '';
        if (title) title.textContent = this.dataset[`${suffix}Title`] || '';
        if (baseMedia) baseMedia.hidden = mode !== 'single';
        if (bundleMedia) bundleMedia.hidden = mode !== 'bundle';
        if (bundle3Media) bundle3Media.hidden = mode !== 'bundle3';
        if (baseDescription) baseDescription.hidden = mode !== 'single';
        if (bundleDescription) bundleDescription.hidden = mode !== 'bundle';
        if (bundle3Description) bundle3Description.hidden = mode !== 'bundle3';
        if (baseDetails) baseDetails.hidden = mode !== 'single';
        if (bundleDetails) bundleDetails.hidden = mode !== 'bundle';
        if (bundle3Details) bundle3Details.hidden = mode !== 'bundle3';
        if (fullLink) fullLink.href = this.dataset[`${suffix}Url`] || this.dataset.productUrl;

        this.setState('choose');
      }

      setState(state) {
        const labels = {
          choose: 'Choose powers',
          loading: 'Checking…',
          ready: 'Add to cart',
          soldout: 'Sold out',
          unavailable: 'Unavailable',
        };
        const ready = state === 'ready';
        this.dataset.state = state;
        this.variantInput.disabled = !ready;
        if (!ready) this.variantInput.value = '';
        this.submitButton.disabled = !ready;
        this.submitButton.setAttribute('aria-disabled', String(!ready));
        if (this.submitText) this.submitText.textContent = labels[state];
      }

      setError(message) {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        const error = wrapper?.querySelector('.product-form__error-message');
        if (!wrapper || !error) return;
        error.textContent = message;
        wrapper.hidden = false;
      }

      clearError() {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        if (wrapper) wrapper.hidden = true;
      }
    }
  );
}
